import { useEffect, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { observer } from 'mobx-react';
import boost from 'highcharts/modules/boost';
import noDataToDdisplay from 'highcharts/modules/no-data-to-display';
import { useTranslation } from 'react-i18next';
import { filter, find, forEach, isEmpty, uniqBy } from 'lodash';
import Highcharts, { Options, Series } from 'highcharts';
import moment from 'moment';
import { Tabs, theme } from 'antd';
import useResizeObserver from 'use-resize-observer';
import { useSearchParams } from 'react-router-dom';
import { useStores } from '@/hooks/use-stores';
import { useInvestigationSync } from '@/hooks/useInvestigationSync';
import { useMeasurement } from '@/hooks/useMeasurement';
import { metricsDetails } from '@/constants';
import { getInvestigationDashboardChartColors } from '@/theme';
import { MetricType } from '@/constants/metric-type.enum';

import { AlertLevels } from '@/constants/enums';

import ActionBar from './TabPane/ActionBar/ActionBar';
import Graph from './TabPane/Graph';
import TabularData from './TabPane/TabularData';
import MetricsTooltip from '../MetricsTooltip/MetricsTooltip';
import { Ylabel } from '@/comp/Trends/Ylabel';
import UTCOffsetText from './TabPane/UTCOffsetText';
import AlertsTable from './TabPane/AlertsTable';
import { getInitOptions } from './TabPane/getInitOptions';
import SeverityIcon from '../common/icons/SeverityIcon';
import { wrapHighchartsLabel } from '@/comp/InvestigationDashboard/TabPane/utils/WrapHighChartsLabel';
import { buildEngineStatePlotBands, buildEngineStatePlotLines } from './TabPane/utils/engineStateChartUtils';
import { convertSpecificPowerData } from '../Trends/TrendUtils';
import { EngineStateTooltip, useEngineStateTooltip } from './TabPane/EngineStateTooltip';
import { Container } from './InvestigationTabPane.styles';
import ReviewLeakAuditModal from '@/comp/pages/ValueDashboardPage/Leakage/ReviewLeakAuditModal';

boost(Highcharts);
noDataToDdisplay(Highcharts);
Highcharts.AST.allowedAttributes.push('viewBox', 'fill-rule', 'rx', 'transform', 'data-tooltip', 'src', 'alt');
Highcharts.AST.allowedTags.push('comment', 'g', 'img');

(function (H) {
  const panHandler = function (proceed, e, z) {
    setTimeout(() => {
      const visibleAxis = find(this.xAxis, axis => find(axis.series, 'visible'));

      if (!visibleAxis) return;

      this.xAxis.forEach(axis => {
        const axisDataMin = axis.series[0].xData[0];
        const newMin = axisDataMin + (visibleAxis.min - visibleAxis.dataMin);
        const newMax = axisDataMin + (visibleAxis.max - visibleAxis.dataMin);

        axis.setExtremes(newMin, newMax, true, false);
      });
    }, 0);

    proceed.call(this, e, z);
  };

  H.wrap(H.Chart.prototype, 'pan', panHandler);
})(Highcharts);

const getResolutionLabel = (resolutionInMinutes: number, t: (key: string, args: Record<string, unknown>) => string) => {
  if (resolutionInMinutes < 60) {
    return t('minuteAverageWithCount', { count: resolutionInMinutes });
  }
  if (resolutionInMinutes <= 24 * 60) {
    return t('hourAverageWithCount', { count: resolutionInMinutes / 60 });
  }
};

const generateEmptySamplesToMatchSeriesLength = (seriesToMatch, realSamplesCount, dateToFill) =>
  seriesToMatch
    .filter((_, i) => i > realSamplesCount - 1) // select only the difference from the other series
    .map(item => [
      moment(item?.[0]).set({ date: dateToFill.date, months: dateToFill.months, years: dateToFill.years }).valueOf(),
      null, // fill the series that includes today with null value
    ]);

// fill one of the series when one of the selected periods includes current day to keep hours between the series synced
// (data is available only up until the current time)
const fillSeries = (periodsCompareIncludesToday, series = [], otherSeries = []) => {
  const dataSeriesDifferenceCount = periodsCompareIncludesToday ? otherSeries?.length - series?.length : 0;
  if (dataSeriesDifferenceCount) {
    const dateToFill = moment().toObject();
    return [...series, ...generateEmptySamplesToMatchSeriesLength(otherSeries, series?.length, dateToFill)];
  }
  return series;
};

export interface HighchartsTooltip {
  hide: () => void;
  options: {
    enabled: boolean;
  };
}

export interface TypedHighcharts {
  setSize: (width: number) => void;
  resetZoomButton: boolean;
  series: Series[];
  options: Options;
  showNoData: () => void;
  hideNoData: () => void;
  zoomOut: () => void;
  zoom: () => void;
  redraw: () => void;
  destroy: () => void;
  tooltip?: HighchartsTooltip;
}

const InvestigationTabPane = observer(() => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { investigationStore, stationStore } = useStores();
  const { token } = theme.useToken();
  const chartColors = getInvestigationDashboardChartColors(token);
  const [options, setOptions] = useState(getInitOptions(token));
  const [chart, setChart] = useState<TypedHighcharts>();
  const [isStackMode, setIsStackMode] = useState(true);
  const [isZoomed, setIsZoomed] = useState(true);
  const [selectedAlarm, setSelectedAlarm] = useState();
  const [activeTab, setActiveTab] = useState(investigationStore.activeTab ? investigationStore.activeTab : 'tabular');
  const [lastHiddenSeriesDate, setLastHiddenSeriesDate] = useState<Date>();
  const [allAlerts, setAllAlerts] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { tunit, tvalue, measurementSystem } = useMeasurement();
  const [isReviewLeakAuditModalOpen, setIsReviewLeakAuditModalOpen] = useState(false);
  const [reviewLeakAuditDates, setReviewLeakAuditDates] = useState<{
    startDate?: string;
    endDate?: string;
    leakageId?: string;
  }>({});

  useInvestigationSync(investigationStore);

  const onChangeTab = value => {
    setActiveTab(value);
    investigationStore.setActiveTab(value);
  };

  const deepLinkParamsRef = useRef<{
    processed: boolean;
  } | null>(null);

  useEffect(() => {
    const leakageIdParam = searchParams.get('leakageId');
    if (
      !leakageIdParam ||
      deepLinkParamsRef.current?.processed ||
      !investigationStore.dates1?.length ||
      investigationStore.dates1?.length !== 2
    )
      return;

    setReviewLeakAuditDates({
      startDate: investigationStore.dates1[0],
      endDate: investigationStore.dates1[1],
      leakageId: leakageIdParam || undefined,
    });
    setIsReviewLeakAuditModalOpen(true);

    searchParams.delete('leakageId');
    deepLinkParamsRef.current = { processed: true };
  }, [investigationStore.dates1, investigationStore.hasSeries, setSearchParams]);

  const getMetricMinValue = (metric: string) => {
    const metricMinValues = {
      dewpointTemperatureCelsius: tvalue(-100, 'celsius'),
    };

    return metricMinValues[metric] ?? undefined;
  };

  const containerRef = useRef<HTMLDivElement>(null);

  const { tooltipState } = useEngineStateTooltip({
    containerRef,
    enabled: investigationStore.showEngineStatus,
    chart: chart || null,
  });

  useEffect(() => {
    wrapHighchartsLabel();
  }, []);
  const initialChartSize = window.innerWidth * (stationStore.isSidebarCollapsed ? 0.88 : 0.73);

  // Replace the old resize observer with the new implementation
  const { width = initialChartSize } = useResizeObserver({ ref: containerRef });

  // Consolidated chart resizing effect
  useEffect(() => {
    if (!chart) return;
    chart.setSize(options.series.length ? width : initialChartSize);
  }, [chart, width, options.series.length, initialChartSize]);

  useEffect(() => {
    if (!chart) return;

    Highcharts.addEvent(chart, 'redraw', () => setIsZoomed(!!chart?.resetZoomButton));
    return () => {
      Highcharts.removeEvent(chart, 'redraw');
    };
  }, [chart]);

  useEffect(() => {
    const withAlerts = (series, alerts) => {
      const newSeries = series.map((item, index) => {
        const isLast = index + 1 === series?.length;
        const time = isLast ? moment(item[0]).utc().add(1, 'day').startOf('day').valueOf() : item[0];
        const itemAlerts = filter(alerts, { openingTimeRounded: time });
        const warning = filter(itemAlerts, { severity: AlertLevels.Warning });
        const shutdown = filter(itemAlerts, { severity: AlertLevels.Shutdown });
        const service = filter(itemAlerts, { severity: AlertLevels.Service });
        const sortedAlerts = (() => {
          switch (selectedAlarm) {
            case AlertLevels.All:
              return [...shutdown, ...warning, ...service];

            case AlertLevels.Shutdown:
              return [...shutdown];

            case AlertLevels.Warning:
              return [...warning];

            case AlertLevels.Service:
              return [...service];

            case 'none':
              return [];

            default:
              return [];
          }
        })();

        const newItem = isEmpty(itemAlerts)
          ? {
              x: item[0],
              y: item[1],
              dataLabels: {
                enabled: false,
              },
            }
          : {
              x: item[0],
              y: item[1],
              alerts: sortedAlerts,
              dataLabels: {
                enabled: true,
                y: -5,
                useHTML: true,
                formatter: function () {
                  return ReactDOMServer.renderToString(<SeverityIcon severity={sortedAlerts[0]?.severity} />);
                },
              },
            };

        return newItem;
      });

      return newSeries;
    };

    const getSeries = () => {
      const convertData = (data, from) => data.map(item => [item[0], tvalue(item[1], from)]);
      const alertRecords = [];
      const periodsCompareIncludesToday =
        investigationStore.dates1?.length &&
        investigationStore.dates2?.length &&
        [...investigationStore.dates1, ...investigationStore.dates2].some(date => moment().isSame(date, 'day'));
      const series = [
        ...investigationStore.data1.data.map((data, index) => {
          const filledSeries = fillSeries(
            periodsCompareIncludesToday,
            data.series,
            investigationStore.getSecondPeriodSeriesByIndex(index),
          );
          const convertedSeries =
            data.metric === MetricType.SpecificPower
              ? convertSpecificPowerData(tvalue, filledSeries, metricsDetails[data.metric]?.unit)
              : convertData(filledSeries, metricsDetails[data.metric]?.unit);
          const seriesWithAlerts = withAlerts(convertedSeries, data.alerts);
          forEach(data.alerts, alert => alertRecords.push(alert));
          const id = data.device1Id
            ? `0-${data.device1Id}-${data.device2Id}-${data.metric}`
            : `0-${data.deviceId}-${data.metric}`;

          return {
            id,
            name: data.dataType === 'pressureDrop' ? t('Pressure drop') : `${t(metricsDetails[data.metric]?.text)}`,
            deviceName: t(data.deviceName),
            xAxis: 0,
            yAxis: metricsDetails[data.metric]?.unit,
            data: seriesWithAlerts,
            marker: {
              enabled: false,
              lineColor: undefined,
            },
            events: {
              legendItemClick: () => setLastHiddenSeriesDate(new Date()),
            },
            color: chartColors[index],
          };
        }),
        ...investigationStore.data2.data.map((data, index) => {
          const filledSeries = fillSeries(
            periodsCompareIncludesToday,
            data.series,
            investigationStore.getFirstPeriodSeriesByIndex(index),
          );
          const convertedSeries =
            data.metric === MetricType.SpecificPower
              ? convertSpecificPowerData(tvalue, filledSeries, metricsDetails[data.metric]?.unit)
              : convertData(filledSeries, metricsDetails[data.metric]?.unit);
          const seriesWithAlerts = withAlerts(convertedSeries, data.alerts);
          forEach(data.alerts, alert => alertRecords.push(alert));
          const id = data.device1Id
            ? `1-${data.device1Id}-${data.device2Id}-${data.metric}`
            : `1-${data.deviceId}-${data.metric}`;

          return {
            id,
            name: data.dataType === 'pressureDrop' ? t('Pressure drop') : `${t(metricsDetails[data.metric]?.text)}`,
            deviceName: t(data.deviceName),
            xAxis: 1,
            yAxis: metricsDetails[data.metric]?.unit,
            data: seriesWithAlerts,
            marker: {
              enabled: false,
              lineColor: undefined,
            },
            events: {
              legendItemClick: () => setLastHiddenSeriesDate(new Date()),
            },
            color: chartColors[investigationStore.data1.data?.length + index],
          };
        }),
      ];

      setAllAlerts(uniqBy(alertRecords, 'id'));
      return isEmpty(series) ? [] : series;
    };

    const getXAxisEnd = () => {
      const periodEndIso = investigationStore.utcFormattedDates1?.[1];
      const endFromDates = periodEndIso ? moment(periodEndIso).valueOf() : null;
      if (endFromDates) return endFromDates;

      const dataArr = investigationStore.data1?.data;
      const xs =
        dataArr?.flatMap(d => (Array.isArray(d?.series) ? d.series.map(p => p?.[0]).filter(Boolean) : [])) ?? [];
      return xs.length ? Math.max(...xs) : null;
    };

    const getXAxisStart = () => {
      const dataArr = investigationStore.data1?.data;
      const xs =
        dataArr?.flatMap(d => (Array.isArray(d?.series) ? d.series.map(p => p?.[0]).filter(Boolean) : [])) ?? [];
      return xs.length ? Math.min(...xs) : null;
    };

    const engineStateOptions = {
      engineStateChangePoints: investigationStore.engineStateChangePoints,
      showEngineStatus: investigationStore.showEngineStatus,
      getXAxisStart,
      getXAxisEnd,
      token,
      t,
    };

    const getXaxis = () => {
      const xAxisArr = [];
      const xAxis = {
        title: {
          text: (() => ReactDOMServer.renderToString(<UTCOffsetText />))(),
          align: 'low',
          x: 50,
          useHTML: true,
        },
        type: 'datetime',
        crosshair: {
          color: token.colorTextSecondary,
          width: 1,
        },
        lineWidth: 2,
        lineColor: token.colorBorderSecondary,
        tickColor: token.colorBgContainer,
        labels: {
          style: {
            color: token.colorTextSecondary,
          },
        },
        dateTimeLabelFormats: {
          minute: '%l:%M %p',
          hour: '%l:%M %p',
        },
        maxPadding: 0,
        minPadding: 0,
      };

      if (!isEmpty(investigationStore.data1?.data)) {
        xAxisArr.push({
          ...xAxis,
          plotBands: buildEngineStatePlotBands(engineStateOptions),
          plotLines: buildEngineStatePlotLines(engineStateOptions),
        });
      }

      if (!isEmpty(investigationStore.data2?.data)) {
        xAxisArr.push({
          ...xAxis,
          plotBands: [],
          plotLines: [],
        });
      }

      return isEmpty(xAxisArr)
        ? [
            {
              ...xAxis,
              plotBands: buildEngineStatePlotBands(engineStateOptions),
              plotLines: buildEngineStatePlotLines(engineStateOptions),
            },
          ]
        : xAxisArr;
    };

    const getYaxis = () => {
      const uniqData = uniqBy(
        investigationStore.data1.data,
        (series: { metric }) => metricsDetails[series?.metric].unit,
      );
      const yAxisArr = uniqData.map((data, index) => ({
        id: metricsDetails[data.metric]?.unit,
        ...(index === 0
          ? {
              gridLineDashStyle: 'longdash',
              gridLineColor: token.colorBorderSecondary,
              gridLineWidth: 1,
              tickAmount: 6,
              startOnTick: true,
              endOnTick: true,
            }
          : {
              gridLineWidth: 0,
            }),
        lineColor: token.colorBorderSecondary,
        tickColor: token.colorBgContainer,
        lineWidth: 2,
        reserveSpace: false,
        opposite: true,
        title: {
          text: t(tunit(metricsDetails[data.metric]?.unit)),
          align: 'high',
          y: -5,
          style: {
            color: chartColors[index],
            fontSize: '12px',
          },
        },
        labels: {
          useHTML: true,
          formatter: o => {
            return ReactDOMServer.renderToString(<Ylabel value={o.value} token={token} />);
          },
        },
        min: getMetricMinValue(data.metric),
      }));

      return isEmpty(yAxisArr) ? [{}] : yAxisArr;
    };

    const newOption = {
      ...getInitOptions(token),
      chart: {
        ...getInitOptions(token).chart,
        type: isStackMode ? 'area' : 'line',
      },
      series: getSeries(),
      xAxis: getXaxis(),
      yAxis: getYaxis(),
      tooltip: {
        shared: true,
        useHTML: true,
        borderColor: undefined,
        borderWidth: 0,
        borderRadius: 0,
        backgroundColor: undefined,
        hideDelay: 100,
        outside: true,
        positioner: (_, __, point) => ({ x: point.plotX + 10, y: point.plotY }),
        formatter: function () {
          const engineStatusInfo = investigationStore.getEngineStatusAtTime(this.x);
          return ReactDOMServer.renderToString(
            <MetricsTooltip
              x={this.x}
              points={this.points}
              resolutionLabel={getResolutionLabel(investigationStore?.data1?.['resolution(minutes)'], t)}
              shouldGroupMetrics
              isStackMode={isStackMode}
              isValidCompareMode={investigationStore.isCompareMode && investigationStore.dates2?.length === 2}
              token={token}
              engineStatusInfo={engineStatusInfo || undefined}
            />,
          );
        },
      },
    };
    setOptions(newOption);
  }, [
    investigationStore.data1,
    investigationStore.data2,
    investigationStore.engineStateChangePoints,
    investigationStore.showEngineStatus,
    language,
    isStackMode,
    selectedAlarm,
    token,
    measurementSystem,
  ]);

  useEffect(() => {
    if (!investigationStore.allowStacking) {
      setIsStackMode(false);
    }
  }, [investigationStore.allowStacking]);

  useEffect(() => {
    if (!chart) return;

    let noDataText;
    const hasSeries =
      (!isEmpty(investigationStore.selectedMetrics) || !isEmpty(investigationStore.selectedPressureDrops)) &&
      !isEmpty(investigationStore.dates1);
    const hasData = (() => {
      let hasData = false;

      investigationStore.data1.data.forEach(data => !isEmpty(data.series) && (hasData = true));
      investigationStore.data2.data.forEach(data => !isEmpty(data.series) && (hasData = true));

      return hasData;
    })();
    const allInvisible = (() => {
      let allInvisible = true;
      chart.series?.forEach(series => series.visible && (allInvisible = false));
      return allInvisible;
    })();

    if (!hasSeries) {
      noDataText = t('Please choose a time period and at least one parameter.');
    } else if (!hasData) {
      noDataText = t('No data to show for this time frame and parameters.');
    } else if (allInvisible) {
      noDataText = t('Please choose at least one graph.');
    } else {
      noDataText = '';
    }
    chart.options.lang.noData = noDataText;
    chart.hideNoData();

    // set noData visibility according to state
    if (!investigationStore.isLoading) {
      chart.showNoData();
    }
  }, [
    chart,
    investigationStore.isLoading,
    investigationStore.selectedMetrics,
    investigationStore.selectedPressureDrops,
    investigationStore.dates1,
    investigationStore.data1,
    investigationStore.data2,
    lastHiddenSeriesDate,
    language,
  ]);

  useEffect(() => {
    // reset zoom on every date change (period 1 or 2)
    chart?.zoomOut();
  }, [investigationStore.data1, investigationStore.data2, chart, language]);

  const tabItems = [
    ...(stationStore.isWasteWaterStation
      ? []
      : [
          {
            label: t('Tabular'),
            key: 'tabular',
            children: (
              <TabularData
                activityMetricsPeriod1={investigationStore.deviceStatus}
                activityMetricsPeriod2={investigationStore.deviceStatus2}
              />
            ),
          },
        ]),
    {
      label: t('Alerts Record List'),
      key: 'alerts-record-list',
      children: <AlertsTable alerts={allAlerts} loading={investigationStore.isLoading} />,
    },
  ];

  return (
    <Container ref={containerRef} $isLoading={investigationStore.isLoading}>
      <ActionBar isStackMode={isStackMode} setIsStackMode={setIsStackMode} setSelectedAlarm={setSelectedAlarm} />
      <Graph width={width} options={options} setChart={setChart} isZoomed={isZoomed} t={t} />
      <Tabs
        activeKey={activeTab}
        animated={{
          inkBar: true,
          tabPane: false,
        }}
        onChange={onChangeTab}
        items={tabItems}
      />
      <EngineStateTooltip data={tooltipState.data} position={tooltipState.position} visible={tooltipState.visible} />
      <ReviewLeakAuditModal
        open={isReviewLeakAuditModalOpen}
        onClose={() => {
          setIsReviewLeakAuditModalOpen(false);
          setReviewLeakAuditDates({});
        }}
        startDate={reviewLeakAuditDates.startDate}
        endDate={reviewLeakAuditDates.endDate}
        leakageId={reviewLeakAuditDates.leakageId}
      />
    </Container>
  );
});

export default InvestigationTabPane;
